import React, { useState } from "react";
import { Controller } from "react-hook-form";
import PlaceholderModal, { PlaceholderData } from "./PlaceHolderModal";
import { Button } from "@shared/ui/button";

export interface TextareaWithPlaceholderProps {
  name: string;
  control: any;
  label?: string;
  placeholder?: string;
  placeholders?: PlaceholderData;
  required?: boolean;
  helperText?: string;
  className?: string;
  rows?: number;
  cols?: number;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  readonly?: boolean|any;
  onFieldChange?: (fieldName: string, value: any) => void;
}

export const TextareaWithPlaceholder: React.FC<
  TextareaWithPlaceholderProps
> = ({
  name,
  control,
  label,
  placeholder,
  placeholders = [],
  required,
  helperText,
  className = "",
  rows = 4,
  cols,
  minLength,
  maxLength,
  disabled,
  readonly,
  onFieldChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [hasPlaceholders, setHasPlaceholders] = useState(false);

  /**
   * Insert placeholder at cursor position and update form value
   */
  const handleInsertPlaceholder = (
    placeholderText: string,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    // Build new text
    const newText =
      currentValue.substring(0, start) +
      placeholderText +
      currentValue.substring(end);

    // Update form value using onChange from Controller
    onChange(newText);

    // Mark that field has placeholders (for visual feedback)
    setHasPlaceholders(newText.includes("{{") && newText.includes("}}"));

    // Call onFieldChange if provided
    if (onFieldChange) {
      onFieldChange(name, newText);
    }

    // Set cursor position after placeholder
    setTimeout(() => {
      const newPosition = start + placeholderText.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentLength = String(field.value || "").length;
        const isNearingLimit = maxLength && currentLength > maxLength * 0.9;
        const isAtLimit = maxLength && currentLength >= maxLength;

        // Check if current value has placeholders
        const currentValue = field.value || "";
        const valueHasPlaceholders =
          typeof currentValue === "string" &&
          currentValue.includes("{{") &&
          currentValue.includes("}}");

        return (
          <>
            {/* Button Row - Only if placeholders exist */}
            {placeholders && placeholders.length > 0 && (
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{name==="message" ? "Message" : "Email Body"}</label>
                <Button
                type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(true)}
                >
                  <span className="text-base leading-none">+</span>
                  Insert Placeholder
                </Button>
              </div>
            )}

            {/* Textarea Field */}
            <textarea
              {...field}
              ref={textareaRef}
              id={name}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readonly}
              minLength={minLength}
              rows={rows}
              cols={cols}
              onChange={(e) => {
                field.onChange(e);
                setHasPlaceholders(
                  e.target.value.includes("{{") && e.target.value.includes("}}")
                );
                if (onFieldChange) {
                  onFieldChange(name, e.target.value);
                }
              }}
              aria-label={label}
              aria-required={required}
              className={`
                w-full px-3 py-2 border rounded-lg
                text-sm placeholder-gray-400
                focus:outline-none transition-all duration-150
                resize-none
                ${
                  disabled
                    ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
                    : "bg-white"
                }
                ${readonly ? "bg-gray-50 cursor-default border-gray-300" : ""}
                ${
                  valueHasPlaceholders
                    ? "border-blue-400 bg-blue-50/30 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                }
                ${className}
              `}
            />

            {/* Placeholder Modal */}
            <PlaceholderModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              placeholders={placeholders}
              onInsert={(placeholderText) => {
                handleInsertPlaceholder(
                  placeholderText,
                  field.value || "",
                  field.onChange
                );
                setIsModalOpen(false);
              }}
              fieldType="textarea-with-placeholder"
            />
          </>
        );
      }}
    />
  );
};

export default TextareaWithPlaceholder;